<template>
  <div v-if="dropEvent" class="automation-card">
    <div class="header">
      <v-icon>mdi-lightning-bolt</v-icon>
      <vs-heading>Automation</vs-heading>
      <span class="drop-timestamp" data-test="drop-timestamp"> {{ dropTimeLabel }}</span>
    </div>

    <div class="metrics-panel">
      <div class="metric">
        <span class="metric-label">Volume Hauled</span>
        <span class="metric-value" data-test="volume-hauled"> {{ dropDetails.drop }}</span>
      </div>
      <div class="metric">
        <span class="metric-label">Level Max/Min</span>
        <span class="metric-value" data-test="level-range"> {{ dropRangeLabel }}</span>
      </div>
      <div class="metric">
        <span class="metric-label">Unload Time</span>
        <span class="metric-value" data-test="unload-time"> {{ dropDuration }}</span>
      </div>
    </div>

    <div v-if="sensorIds && data && sensorIds.length > 0 && capacity != null" class="metrics-panel">
      <sensor-readings-chart
        class="sensor-chart"
        :sensor-ids="sensorIds"
        :organization-id="data.organizationId"
        :window-start="dropWindow.start"
        :window-end="dropWindow.end"
        :capacity="capacity"
      />
    </div>

    <JobPredictionLinks
      v-if="data"
      :trigger-organization-id="data.organizationId"
      :trigger-url="data.triggerConfigurationLink"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { getJobEventsQuery } from '@/api/jobs'
import { useTriggerConfigurationQuery } from '@/api/sensors'
import moment, { Moment } from 'moment'
import { formatDuration } from '@/utils/date-formatting'
import SensorReadingsChart from '@/components/jobs/SensorReadingsChart.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import JobPredictionLinks from './JobPredictionLinks.vue'

interface Props {
  jobId: string
  jobActivatedAt: Moment | null
  jobCompletedAt: Moment | null
}
const props = defineProps<Props>()

const { data: jobEvents } = getJobEventsQuery(props.jobId)

const dropEvent = computed(() => {
  const events = jobEvents.value
  if (!events) return null

  return events.find((event) => event.type === 'TriggerThresholdResolvedEvent')
})

const triggerId = computed<string | undefined>(() => dropEvent.value?.data?.trigger_id)
const { data } = useTriggerConfigurationQuery(triggerId)

const sensorIds = computed(() => {
  return dropEvent.value?.data?.sensor_ids || []
})
const capacity = computed(() => data.value?.capacity ?? undefined)

const dropDetails = computed(() => {
  const max = dropEvent.value?.data?.max || 0
  const min = dropEvent.value?.data?.min || 0

  return {
    max: max.toFixed(2),
    min: min.toFixed(2),
    drop: (max - min).toFixed(2),
  }
})

const dropRangeLabel = computed(() => {
  if (dropDetails.value.drop === '0.00') return 'N/A'
  return `${dropDetails.value.max} - ${dropDetails.value.min}`
})

const dropTimeLabel = computed(() => {
  if (dropDetails.value.drop === '0.00') return 'No Drop Detected'
  const dateTime = moment.utc(dropEvent.value?.data?.end).local().format('MMM D, YYYY h:mm A')
  return `Tanks unloaded ${dateTime}`
})

const dropWindow = computed(() => {
  const { start, end } = dropEvent.value?.data ?? {}

  return {
    start: moment
      .utc(start ?? props.jobActivatedAt)
      .subtract(1, 'hour')
      .toDate(),
    end: moment
      .utc(end ?? props.jobCompletedAt)
      .add(1, 'hour')
      .toDate(),
  }
})

const dropDuration = computed(() => {
  const start = dropEvent.value?.data?.start
  const end = dropEvent.value?.data?.end

  if (!start || !end) return 'N/A'

  const duration = moment.duration(moment.utc(end).diff(moment.utc(start)))
  return formatDuration(duration, 2, true)
})
</script>

<style scoped>
.automation-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 16px;
  font-family: var(--font-regular);
  background: linear-gradient(135deg, #dfebfd 0%, #e8f1fe 60%, #edf4fe 100%);
  border: 1px solid #cde0fb;
}

.header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.header .v-icon {
  color: var(--color-blue);
}

.drop-timestamp {
  padding-top: 2px;
  font-size: 12px;
  color: var(--color-grey);
}
.metrics-panel {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(6, 95, 212, 0.1);
  gap: 12px 24px;
  flex-wrap: wrap;
  justify-content: space-between;
}

.metric {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.metric-label {
  font-size: 14px;
  color: var(--color-grey);
}

.metric-value {
  font-size: 15px;
  font-weight: 700;
}

.sensor-chart {
  flex: 1 1 100%;
  min-width: 0;
}
</style>
